// Dnes: jedno volání GET /v1/today. Splněno přímo z Dnes aktualizuje data lokálně hned,
// pak se obrazovka tiše obnoví ze serveru. Při chybě se lokální změna vrátí.
import { defineStore } from 'pinia'
import { ref } from 'vue'
import type { TodayResponse } from '@wac-buddy/shared'
import { getToday } from '@/api/today'
import { updatePromise } from '@/api/promises'
import { updateTask } from '@/api/tasks'
import { useClientsStore } from './clients'
import { useJobsStore } from './jobs'

export type TodayStatus = 'idle' | 'loading' | 'ready' | 'error'

export const useTodayStore = defineStore('today', () => {
  const data = ref<TodayResponse | null>(null)
  const status = ref<TodayStatus>('idle')

  let requestSeq = 0

  /** `silent`: data už jsou vidět, stav `loading` se nepřepíná a chyba obrazovku nerozbije. */
  async function load(silent = false): Promise<void> {
    const seq = ++requestSeq
    if (!silent || !data.value) status.value = 'loading'
    try {
      const res = await getToday()
      if (seq !== requestSeq) return
      data.value = res
      status.value = 'ready'
    } catch {
      if (seq !== requestSeq) return
      if (!silent || !data.value) status.value = 'error'
    }
  }

  function reload(): Promise<void> {
    return load(true)
  }

  function snapshot(): TodayResponse | null {
    return data.value ? (JSON.parse(JSON.stringify(data.value)) as TodayResponse) : null
  }

  async function markPromiseDone(id: string): Promise<void> {
    const before = snapshot()
    const d = data.value
    if (d) {
      const wasOverdue = d.hot.overduePromises.some((p) => p.id === id)
      d.hot.overduePromises = d.hot.overduePromises.filter((p) => p.id !== id)
      d.hot.todayPromises = d.hot.todayPromises.filter((p) => p.id !== id)
      if (wasOverdue) d.kpi.overdue = Math.max(0, d.kpi.overdue - 1)
    }
    try {
      const saved = await updatePromise(id, { status: 'done' })
      if (saved.clientId) delete useClientsStore().details[saved.clientId]
      if (saved.jobId) delete useJobsStore().details[saved.jobId]
    } catch (e) {
      if (before) data.value = before
      throw e
    }
    await reload()
  }

  async function markTaskDone(id: string): Promise<void> {
    const before = snapshot()
    const d = data.value
    if (d) {
      const wasOverdue = d.hot.overdueTasks.some((t) => t.id === id)
      d.hot.overdueTasks = d.hot.overdueTasks.filter((t) => t.id !== id)
      if (wasOverdue) d.kpi.overdue = Math.max(0, d.kpi.overdue - 1)
    }
    try {
      const saved = await updateTask(id, { status: 'done' })
      if (saved.jobId) delete useJobsStore().details[saved.jobId]
    } catch (e) {
      if (before) data.value = before
      throw e
    }
    await reload()
  }

  /** Tiše obnoví Dnes, ale jen když už je načtené (jinak ho načte až obrazovka při otevření). */
  function refresh(): Promise<void> {
    return data.value ? load(true) : Promise.resolve()
  }

  function invalidate(): void {
    requestSeq++
    data.value = null
    status.value = 'idle'
  }

  return { data, status, load, reload, refresh, markPromiseDone, markTaskDone, invalidate }
})
